import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, User, LogOut, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { logout, getSession } from '@/lib/store';

interface AccountDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  /** Position: dropdown anchor reference (the button element) */
  anchorRef: React.RefObject<HTMLElement | null>;
}

export function AccountDropdown({ isOpen, onClose, anchorRef }: AccountDropdownProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isRTL = i18n.language === 'ar';
  const session = getSession();

  const displayName = session?.metadata?.demo
    ? t('auth.demo.account_name')
    : (session?.name || 'Amine B.');
  const email = session?.email || 'admin@charikaty.com';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen, onClose, anchorRef]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        anchorRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose, anchorRef]);

  // Trap focus within dropdown when open
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const firstFocusable = dropdownRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }
  }, [isOpen]);

  const handleSettings = () => {
    onClose();
    navigate('/dashboard/settings');
  };

  const handleProfile = () => {
    onClose();
    navigate('/dashboard/settings');
  };

  const handleLogout = () => {
    onClose();
    logout();
  };

  const menuItems = [
    {
      id: 'settings',
      label: t('common.settings'),
      icon: Settings,
      action: handleSettings,
      className: 'text-text-primary',
    },
    {
      id: 'profile',
      label: t('dashboard.account_menu.profile', { defaultValue: i18n.language === 'ar' ? 'الملف الشخصي' : i18n.language === 'en' ? 'Profile' : 'Mon profil' }),
      icon: User,
      action: handleProfile,
      className: 'text-text-primary',
    },
    {
      id: 'logout',
      label: t('common.logout'),
      icon: LogOut,
      action: handleLogout,
      className: 'text-red-400 hover:!text-red-300',
      isDanger: true,
    },
  ];

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      role="menu"
      aria-label={t('dashboard.account_menu.aria_label', { defaultValue: 'Account menu' })}
      className={cn(
        'absolute top-full mt-2 z-[9999] w-64',
        'bg-bg-secondary border border-border rounded-2xl shadow-2xl shadow-black/40',
        'animate-in fade-in slide-in-from-top-2 duration-150',
        isRTL ? 'left-0' : 'right-0'
      )}
    >
      {/* User info header */}
      <div className="px-4 py-3.5 border-b border-border/60">
        <div className={cn('flex items-center gap-3', isRTL && 'flex-row-reverse')}>
          <div className="relative shrink-0">
            <div className="w-10 h-10 bg-accent-primary rounded-xl flex items-center justify-center shadow-lg shadow-accent-primary/25 text-white font-bold text-sm">
              {initials}
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-bg-secondary" />
          </div>
          <div className={cn('flex flex-col min-w-0', isRTL && 'text-right')}>
            <p className="text-[13px] font-bold text-text-primary leading-none mb-1 truncate">
              {displayName}
            </p>
            <p className="text-[11px] text-text-muted truncate leading-none">{email}</p>
          </div>
        </div>

        {/* Role badge */}
        <div className={cn('flex mt-2.5', isRTL ? 'justify-end' : 'justify-start')}>
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-accent-primary/10 border border-accent-primary/20 text-accent-primary text-[10px] font-bold uppercase ltr:tracking-wider rtl:tracking-normal">
            <Shield size={9} />
            {t('auth.profiles.ENTERPRISE')}
          </span>
        </div>
      </div>

      {/* Menu items */}
      <div className="py-1.5 px-1.5" role="none">
        {menuItems.map((item, index) => {
          const Icon = item.icon;
          const isLast = index === menuItems.length - 1;
          return (
            <div key={item.id}>
              {isLast && (
                <div className="h-px bg-border/60 mx-1 my-1" />
              )}
              <button
                role="menuitem"
                onClick={item.action}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150',
                  'hover:bg-bg-tertiary focus:outline-none focus:bg-bg-tertiary focus-visible:ring-2 focus-visible:ring-accent-primary/50',
                  'active:scale-[0.98]',
                  item.className,
                  isRTL && 'flex-row-reverse text-right',
                  item.isDanger && 'hover:bg-red-500/10 focus:bg-red-500/10'
                )}
              >
                <Icon
                  size={16}
                  className={cn(
                    'shrink-0 transition-colors',
                    item.isDanger ? 'text-red-400' : 'text-text-muted group-hover:text-text-primary'
                  )}
                />
                <span className="flex-1 truncate">{item.label}</span>
              </button>
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2.5 border-t border-border/60">
        <p className={cn('text-[9px] text-text-muted uppercase ltr:tracking-widest rtl:tracking-normal font-bold', isRTL && 'text-right')}>
          Charikaty Sovereign OS • v2.0
        </p>
      </div>
    </div>
  );
}
